import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Voice demo signup form (#7039 v2): shows the chosen lead type's demo
// persona fields and fills them with Faker data from
// GET /voice_demo/generate_data on the "Generate demo data" button.
//
// One server-rendered field group per lead type; only the selected type's
// group is visible AND enabled — hidden groups stay disabled so their inputs
// never submit. Generated values land in plain text inputs, so the agent can
// edit anything before submitting.
//
// Usage (on the signup <form>):
//   form[data-controller="voice-demo-data"
//        data-voice-demo-data-url-value="/voice_demo/generate_data"]
//     select[data-voice-demo-data-target="typeSelect"
//            data-action="voice-demo-data#sync"]
//     div[data-voice-demo-data-target="section"]
//       button[data-voice-demo-data-target="generateButton"
//              data-action="voice-demo-data#generate"]
//       div[data-voice-demo-data-target="error"]
//       div[data-voice-demo-data-target="group" data-lead-type="VeteranLead"]
//         input[data-voice-demo-data-field="needed_coverage"]
export default class extends Controller {
  static targets = ["typeSelect", "section", "group", "generateButton", "error"]
  static values = { url: String }

  connect() {
    this.sync()
  }

  // Reveal the selected type's group; hide + disable every other group so
  // only whitelisted fields for the chosen type ever submit.
  sync() {
    const type = this.selectedType()
    let anyVisible = false

    this.groupTargets.forEach((group) => {
      const active = group.dataset.leadType === type
      group.classList.toggle("d-none", !active)
      group.querySelectorAll("input").forEach((input) => { input.disabled = !active })
      if (active) anyVisible = true
    })

    this.sectionTarget.classList.toggle("d-none", !anyVisible)
    this.hideError()
  }

  async generate(event) {
    event.preventDefault()
    const type = this.selectedType()
    if (!type) return

    this.hideError()
    this.generateButtonTarget.disabled = true
    try {
      const response = await get(this.urlValue, {
        query: { lead_type: type },
        responseKind: "json"
      })
      if (!response.ok) throw new Error(`generate_data returned ${response.statusCode}`)

      const { demo_payload: payload } = await response.json
      this.fillActiveGroup(type, payload || {})
    } catch {
      this.errorTarget.classList.remove("d-none")
    } finally {
      this.generateButtonTarget.disabled = false
    }
  }

  fillActiveGroup(type, payload) {
    const group = this.groupTargets.find((g) => g.dataset.leadType === type)
    if (!group) return

    group.querySelectorAll("input[data-voice-demo-data-field]").forEach((input) => {
      const value = payload[input.dataset.voiceDemoDataField]
      if (value === undefined || value === null) return

      input.value = String(value)
    })
  }

  selectedType() {
    return this.typeSelectTarget.value
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("d-none")
  }
}
